const math = require('mathjs');
const feedExtrapolation = require('./feed-extrapolation');
const {
  makeEmptyHeightMap,
  downScaleHeightMap,
  copyHeightMap,
  completeHeightMapAssumingNoFeed,
  combineHeightMaps,
  isHeightMapEmpty,
} = require('./height-map-helpers');
const { makePointCloud } = require('./point-cloud');
const { makeSurface } = require('./surface');
const { FeedHeightMap } = require('./feed-height-map');

/**
 * Create a feed height map from a camera-oriented surface
 * @param {Object} feedSurface the identified feed surface (units in meters)
 * @param {Object} binWallSurface the identified feed surface (units in meters)
 * @param {Object} [unmaskedSurface] the full bin + feed surface (units in meters)
 * @param {BinGeometry} binGeometry  Bin json
 * @param {Number} [resolution]  size of the square matrix to return
 * @param {Number} [pan]  the left-right angle correction to use
 * @param {Number} [tilt]  the up-down angle correction to use
 * @param {Number} [unmaskedHopperSnapDistance] the snap distance where parts unmaskedSurface will snap to bin walls
 * @returns {FeedHeightMap}
 */
exports.surfacesToFeedHeightMap = function ({ feedSurface, binWallSurface, binGeometry, resolution = 50, pan = 0, tilt = 0, unmaskedHopperSnapDistance = 0.5 /* m */ }) {
  feedSurface = binGeometry.changeSurfaceToBinsCoordinateSystem(feedSurface, tilt, pan);
  binWallSurface = binGeometry.changeSurfaceToBinsCoordinateSystem(binWallSurface, tilt, pan);

  const { feedSurfaceHeightMap, binWallHeightMap } = _makeUnextrapolatedHeightMap(resolution, feedSurface, binGeometry, binWallSurface, unmaskedHopperSnapDistance);

  return _makeFeedHeightMap(feedSurfaceHeightMap, binWallHeightMap, resolution, binGeometry);
};

/**
 * Create a feed height map from single camera-oriented depth
 * @param {Object} binGeometry  Bin geometry object
 * @param {Number} centralDepth  the distance in m from the camera to the feed
 * @param {Number} [resolution]  size of the square matrix to return
 * @param {Number} pan  camera pan angle in radians
 * @param {Number} tilt camera tilt angle in radians
 * @returns {HeightMapData}
 */
exports.createHeightMapFromSingleDepth = function ({ binGeometry, centralDepth, resolution = 50, pan, tilt }) {
  const pointCloud = makePointCloud([0], [0], [-centralDepth] /* Co-ordinate from camera's co-ordinate system */);
  const surface = makeSurface(pointCloud, [], [], []);

  const surfaceBinCoords = binGeometry.changeSurfaceToBinsCoordinateSystem(surface, tilt, pan);

  // Get the co-ordinates from the bin's co-ordinate system, where 0, 0, 0 is the bottom of the center of the hopper
  const [x, y, z] = surfaceBinCoords.pointCloud.point(0);

  // Calculate the x, y coordinates of the "point" in the height map coordinate space
  const heightMapCoordinates = binGeometry.binXYCoordinateToHeightMapCoordinate([x, y], resolution).map(Math.floor);
  const heightMapCoords = {
    x: heightMapCoordinates[0],
    y: heightMapCoordinates[1],
    height: z,
  };

  const heightMap = makeEmptyHeightMap(resolution);
  heightMap[heightMapCoords.x][heightMapCoords.y] = heightMapCoords.height;
  feedExtrapolation(heightMap, binGeometry);

  _snapValuesToBin(heightMap, resolution, binGeometry);
  return heightMap;
};

function _makeHeightMapFromSurface(resolution, surface, binGeometry) {
  const heightMap = makeEmptyHeightMap(resolution);

  // Iterate over every triangle in the surface
  for (let index = 0; index < surface.numTriangles(); index++) {
    const triangle = surface.triangle(index);

    // Calculate the plane that the triangle lies on, in the form:
    //    a(x - x0) + b(y - y0) + c(z - z0) = 0
    const plane = _getPlane(triangle);

    // Make a rectangular bounding box from the triangle in the bin's coordinate system
    const minX = math.min(triangle[0][0], triangle[1][0], triangle[2][0]);
    const maxX = math.max(triangle[0][0], triangle[1][0], triangle[2][0]);
    const minY = math.min(triangle[0][1], triangle[1][1], triangle[2][1]);
    const maxY = math.max(triangle[0][1], triangle[1][1], triangle[2][1]);

    // Identify all points in the projected height map that fit in the bounding box:
    const [minXScaled, minYScaled] = binGeometry.binXYCoordinateToHeightMapCoordinate([minX, minY], resolution).map(Math.floor);
    const [maxXScaled, maxYScaled] = binGeometry.binXYCoordinateToHeightMapCoordinate([maxX, maxY], resolution).map(Math.ceil);

    // Test each point in the bounding box to decide whether it lies inside
    // the projection of the triangle on the height map surface
    for (let xm = minXScaled; xm <= maxXScaled; xm++) {
      for (let ym = minYScaled; ym <= maxYScaled; ym++) {
        // Skip any points that are outside the circle formed by projecting the body onto the height map
        if (!binGeometry.isHeightMapCoordinateInBin(xm, ym, resolution)) {
          continue;
        }
        // Convert from feed map coordinate index to bin's coordinate system:
        const [x, y] = binGeometry.heightMapXYCoordinateToBinCoordinate([xm, ym], resolution);

        // Check if this point on the height map is contained within the projected surface of the triangle:
        if (xm >= 0 && xm < resolution && ym >= 0 && ym < resolution
          && _isPointInTriangle(x, y, triangle)) {
          // Use the plane we computed earlier that the triangle lies on to compute the z value at exactly
          // this point
          const heightAtThisPixel = _getZValueFromPlane(x, y, plane);

          // Fill in this height into our height map if we do not have a height
          // yet or if it is lower than the existing height. (I'm assuming lower
          // heights are better because sometimes we miss parts of the sidewall,
          // which would be higher than feed).
          if (heightMap[xm][ym] === null || heightMap[xm][ym] > heightAtThisPixel) {
            heightMap[xm][ym] = heightAtThisPixel;
          }
        }
      }
    }
  }
  return heightMap;
}

function _isPointInTriangle(x, y, triangle) {
  const barycentricCoordinates = _getBarycentricCoordinates(x, y, triangle);
  // At least one barycentric coordinate will be negative if the point lies outside the triangle
  return barycentricCoordinates.every((coord => coord >= 0 && coord <= 1));
}

// See: https://en.wikipedia.org/wiki/Barycentric_coordinate_system
function _getBarycentricCoordinates(x, y, [[x1, y1], [x2, y2], [x3, y3]]) {
  const l1 = ((y2 - y3) * (x - x3) + (x3 - x2) * (y - y3)) / ((y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3));
  const l2 = ((y3 - y1) * (x - x3) + (x1 - x3) * (y - y3)) / ((y2 - y3) * (x1 - x3) + (x3 - x2) * (y1 - y3));
  const l3 = 1 - l1 - l2;

  return [l1, l2, l3];
}


// Calculate the plane that the triangle lies on, in the form:
//    a(x - x0) + b(y - y0) + c(z - z0) = 0
function _getPlane(triangle) {
  // Use two sides of the triangle as vectors
  const vector1 = [triangle[0][0] - triangle[1][0], triangle[0][1] - triangle[1][1], triangle[0][2] - triangle[1][2]];
  const vector2 = [triangle[0][0] - triangle[2][0], triangle[0][1] - triangle[2][1], triangle[0][2] - triangle[2][2]];

  // Get the cross product of those two vectors:
  const cross = math.cross(vector1, vector2);

  // The cross product ends up being the same as [a, b, c] in the plane's equation above, and we can use
  // any point on the plane for x0, y0, z0, so use one of the points in the triangle
  return {
    a: cross[0],
    b: cross[1],
    c: cross[2],
    x0: triangle[0][0],
    y0: triangle[0][1],
    z0: triangle[0][2],
  };
}

function _getZValueFromPlane(x, y, plane) {
  const { a, b, c, x0, y0, z0 } = plane;
  return ((-a * (x - x0) - b * (y - y0)) / c) + z0;
}

function _snapValuesToBin(heightMap, resolution, binGeometry) {
  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (!binGeometry.isHeightMapCoordinateInBin(x, y, resolution)) {
        heightMap[x][y] = null;
        continue;
      }
      if (heightMap[x][y] !== null) {
        const { min, max } = binGeometry.feedHeightConstraint(x, y, resolution);

        if (heightMap[x][y] > max) {
          heightMap[x][y] = max;
        } else if (heightMap[x][y] < min) {
          heightMap[x][y] = min;
        }
      }
    }
  }
}

function _makeUnextrapolatedHeightMap(resolution, feedSurface, binGeometry, binWallSurface, unmaskedHopperSnapDistance) {
  // When we convert the surface into a height map, we want to use a much higher resolution, then downscale it so that
  // if the surface is very small, we get as many filled cells as possible in the height map
  const surfaceToHeightMapScaleFactor = 10;
  const feedSurfaceHeightMap = downScaleHeightMap(binGeometry, _makeHeightMapFromSurface(
    resolution * surfaceToHeightMapScaleFactor,
    feedSurface,
    binGeometry,
  ), surfaceToHeightMapScaleFactor);
  const binWallHeightMap = _makeHeightMapFromSurface(resolution, binWallSurface, binGeometry);

  for (let x = 0; x < resolution; x++) {
    for (let y = 0; y < resolution; y++) {
      if (binWallHeightMap[x][y] !== null) {
        const minFeedHeight = binGeometry.feedHeightConstraint(x, y, resolution).min;
        if (binWallHeightMap[x][y] <= minFeedHeight + unmaskedHopperSnapDistance) {
          binWallHeightMap[x][y] = minFeedHeight;
        }
      }
    }
  }
  return { feedSurfaceHeightMap, binWallHeightMap };
}

/**
 * @param {HeightMapData} feedHeightMapData
 * @param {HeightMapData} binWallHeightMapData
 * @param {Number} resolution
 * @param {BinGeometry} binGeometry
 * @returns {FeedHeightMap}
 */
function _makeFeedHeightMap(feedHeightMapData, binWallHeightMapData, resolution, binGeometry) {
  const extrapolatedHeightMap = copyHeightMap(feedHeightMapData);

  if (isHeightMapEmpty(extrapolatedHeightMap)) {
    completeHeightMapAssumingNoFeed(extrapolatedHeightMap, binGeometry);
  } else {
    feedExtrapolation(extrapolatedHeightMap, binGeometry);
  }

  _snapValuesToBin(extrapolatedHeightMap, resolution, binGeometry);

  const unextrapolatedHeightMap = combineHeightMaps(feedHeightMapData, binWallHeightMapData);
  _snapValuesToBin(unextrapolatedHeightMap, resolution, binGeometry);

  return new FeedHeightMap(extrapolatedHeightMap, unextrapolatedHeightMap, binGeometry);
}
